import { useState, useEffect, useCallback, useRef } from 'react';
import type { AttachmentDownloader, AttachmentMeta } from '../../types/portal';
import { portalService } from '../../services/portal';
import { formatFileSize } from '../../utils/dateFormatters';
import AttachmentPreviewOverlay from './AttachmentPreviewOverlay';
import {
  attachmentOpenMode,
  downloadBlobUrl,
  isPdfAttachment,
  isPreviewableAttachment,
} from './attachmentPreview';
import styles from './AttachmentList.module.css';

interface AttachmentListProps {
  attachments: AttachmentMeta[];
  emailId: string;
  /**
   * How to fetch an attachment's bytes. Defaults to the orders-portal path
   * (by emailId + filename); the Tech Support Co-Pilot passes a live-Gmail
   * downloader keyed on the attachment_id.
   */
  download?: AttachmentDownloader;
  /**
   * When supplied, a previewable attachment (PDF / image) opens INLINE through
   * this callback instead of the full-screen preview overlay. The email pane
   * beside an order passes it so the PO lands next to the order fields and
   * nothing has to be closed before the order is saved (kanban fe62ebea).
   * Non-previewable files still download.
   */
  onOpenInline?: (att: AttachmentMeta, emailId: string) => void;
}

const _portalDownload: AttachmentDownloader = (att, emailId) =>
  portalService.downloadAttachment(emailId, att.filename);

/** Map MIME type to a short label + CSS class. */
function getFileTypeInfo(mimeType: string, filename: string): { label: string; className: string } {
  if (isPdfAttachment(mimeType, filename)) {
    return { label: 'PDF', className: styles.fileIconPdf };
  }
  if (mimeType.startsWith('image/')) {
    return { label: 'IMG', className: styles.fileIconImage };
  }
  if (
    mimeType.includes('wordprocessingml') ||
    mimeType.includes('msword')
  ) {
    return { label: 'DOC', className: styles.fileIconDoc };
  }
  if (
    mimeType.includes('spreadsheetml') ||
    mimeType.includes('ms-excel') ||
    mimeType === 'text/csv'
  ) {
    return { label: 'XLS', className: styles.fileIconSheet };
  }
  if (mimeType.startsWith('text/')) {
    return { label: 'TXT', className: styles.fileIconText };
  }
  // Fallback: use file extension
  const ext = filename.split('.').pop()?.toUpperCase() || '?';
  return { label: ext.substring(0, 4), className: styles.fileIconOther };
}

type PreviewState = {
  blobUrl: string;
  filename: string;
  mimeType: string;
} | null;

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  emailId,
  download = _portalDownload,
  onOpenInline,
}) => {
  const [preview, setPreview] = useState<PreviewState>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const blobUrlsRef = useRef<string[]>([]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const fetchAndCreateBlobUrl = useCallback(
    async (att: AttachmentMeta): Promise<string> => {
      const blob = await download(att, emailId);
      const blobUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.push(blobUrl);
      return blobUrl;
    },
    [emailId, download],
  );

  const handleClick = useCallback(
    async (att: AttachmentMeta, e: React.MouseEvent) => {
      e.preventDefault();
      if (loading) return;

      const previewable = isPreviewableAttachment(att.mime_type, att.filename);
      if (previewable && onOpenInline) {
        // The inline viewer fetches its own bytes; nothing to load here.
        onOpenInline(att, emailId);
        return;
      }

      setLoading(att.filename);
      try {
        const blobUrl = await fetchAndCreateBlobUrl(att);

        if (previewable) {
          setPreview({ blobUrl, filename: att.filename, mimeType: att.mime_type });
        } else {
          downloadBlobUrl(blobUrl, att.filename);
        }
      } catch (err) {
        console.error('Failed to download attachment:', err);
      } finally {
        setLoading(null);
      }
    },
    [emailId, loading, fetchAndCreateBlobUrl, onOpenInline],
  );

  const closePreview = useCallback(() => setPreview(null), []);

  if (attachments.length === 0) return null;

  return (
    <>
      <div className={styles.attachmentSection}>
        <div className={styles.sectionHeader}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
          </svg>
          Attachments
          <span className={styles.badge}>{attachments.length}</span>
        </div>
        <div className={styles.list}>
          {attachments.map((att) => {
            const typeInfo = getFileTypeInfo(att.mime_type, att.filename);
            const isLoading = loading === att.filename;
            const mode = attachmentOpenMode(att, Boolean(onOpenInline));
            return (
              <a
                key={att.filename}
                className={styles.attachmentItem}
                href="#"
                onClick={(e) => handleClick(att, e)}
                title={att.filename}
                style={isLoading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                data-action="attachment_open"
                data-target-email-id={emailId}
                data-outcome={mode === 'download' ? 'request' : 'open'}
                data-record-value="mode"
                data-value={mode}
              >
                <div className={`${styles.fileIcon} ${typeInfo.className}`}>
                  {isLoading ? '...' : typeInfo.label}
                </div>
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{att.filename}</div>
                  <div className={styles.fileMeta}>{formatFileSize(att.size)}</div>
                </div>
                <div className={styles.downloadIcon}>
                  {mode !== 'download' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Full-screen preview (standalone email window only; the pane beside an
          order opens attachments inline through onOpenInline instead). */}
      {preview && (
        <AttachmentPreviewOverlay
          blobUrl={preview.blobUrl}
          filename={preview.filename}
          mimeType={preview.mimeType}
          emailId={emailId}
          onClose={closePreview}
        />
      )}
    </>
  );
};

export default AttachmentList;
